import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, GripVertical, X, Save, ArrowLeft, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { customFieldsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const CustomFieldsManager = () => {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [formData, setFormData] = useState({
    field_name: '',
    field_label: '',
    field_type: 'text',
    is_required: false,
    dropdown_options: [],
    default_value: '',
  });
  const [dropdownInput, setDropdownInput] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const response = await customFieldsAPI.list({ status: 'active' });
      setFields(response.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load custom fields'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        field_name: field.field_name,
        field_label: field.field_label,
        field_type: field.field_type,
        is_required: field.is_required,
        dropdown_options: field.dropdown_options || [],
        default_value: field.default_value || '',
      });
    } else {
      setEditingField(null);
      setFormData({
        field_name: '',
        field_label: '',
        field_type: 'text',
        is_required: false,
        dropdown_options: [],
        default_value: '',
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingField(null);
    setDropdownInput('');
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.field_name.trim()) {
      newErrors.field_name = 'Field name is required';
    } else if (!/^[a-z_][a-z0-9_]*$/.test(formData.field_name)) {
      newErrors.field_name = 'Field name must be lowercase, start with a letter, and contain only letters, numbers, and underscores';
    }

    if (!formData.field_label.trim()) {
      newErrors.field_label = 'Field label is required';
    }

    if (formData.field_type === 'dropdown' && formData.dropdown_options.length === 0) {
      newErrors.dropdown_options = 'At least one dropdown option is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    // Normalize "none" sentinel back to empty string for backend
    const payload = {
      ...formData,
      default_value: formData.default_value === 'none' ? '' : formData.default_value,
    };

    try {
      if (editingField) {
        await customFieldsAPI.update(editingField.id, payload);
        toast.success('Custom field updated successfully');
      } else {
        await customFieldsAPI.create(payload);
        toast.success('Custom field created successfully');
      }
      handleCloseModal();
      fetchFields();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save custom field'));
    }
  };

  const handleDelete = (field) => {
    setConfirmModal({
      title: 'Delete Custom Field',
      description: `Are you sure you want to delete "${field.field_label}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await customFieldsAPI.delete(field.id);
          toast.success('Custom field deleted successfully');
          fetchFields();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete custom field'));
        }
      },
    });
  };

  const handleAddDropdownOption = () => {
    if (!dropdownInput.trim()) return;

    if (formData.dropdown_options.includes(dropdownInput.trim())) {
      toast.error('This option already exists');
      return;
    }

    setFormData(prev => ({
      ...prev,
      dropdown_options: [...prev.dropdown_options, dropdownInput.trim()]
    }));
    setDropdownInput('');
  };

  const handleRemoveDropdownOption = (index) => {
    setFormData(prev => ({
      ...prev,
      dropdown_options: prev.dropdown_options.filter((_, i) => i !== index)
    }));
  };

  const getFieldTypeIcon = (type) => {
    switch (type) {
      case 'text': return '📝';
      case 'number': return '🔢';
      case 'date': return '📅';
      case 'dropdown': return '📋';
      case 'yes_no': return '✅';
      default: return '❓';
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/items')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Custom Fields Manager</h1>
            <p className="text-sm text-gray-600 mt-1">
              Define custom fields that will be available for all items in your business
            </p>
          </div>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Custom Field
        </Button>
      </div>

      {/* Fields List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-3 text-gray-600">Loading custom fields...</p>
        </div>
      ) : fields.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-lg font-semibold mb-2">No Custom Fields Yet</h3>
          <p className="text-gray-600 mb-4">
            Create custom fields to capture additional information specific to your business needs
          </p>
          <Button onClick={() => handleOpenModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Custom Field
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Field Label</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Field Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {fields.map((field) => (
                <tr key={field.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getFieldTypeIcon(field.field_type)}</span>
                      <span className="font-medium">{field.field_label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">{field.field_name}</code>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {field.field_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {field.is_required ? (
                      <span className="text-red-600 font-semibold">Yes</span>
                    ) : (
                      <span className="text-gray-400">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">
                      {field.default_value || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem onClick={() => handleOpenModal(field)}>
                          <Edit2 className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(field)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold">
                {editingField ? 'Edit Custom Field' : 'Add Custom Field'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Field Name */}
              <div>
                <Label htmlFor="field_name">
                  Field Name (Internal) *
                </Label>
                <Input
                  id="field_name"
                  value={formData.field_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, field_name: e.target.value.toLowerCase() }))}
                  placeholder="e.g., warranty_period"
                  disabled={!!editingField}
                  className={errors.field_name ? 'border-red-500' : ''}
                />
                {errors.field_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.field_name}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Lowercase, letters, numbers, underscores only. Cannot be changed after creation.
                </p>
              </div>

              {/* Field Label */}
              <div>
                <Label htmlFor="field_label">
                  Field Label (Display Name) *
                </Label>
                <Input
                  id="field_label"
                  value={formData.field_label}
                  onChange={(e) => setFormData(prev => ({ ...prev, field_label: e.target.value }))}
                  placeholder="e.g., Warranty Period"
                  className={errors.field_label ? 'border-red-500' : ''}
                />
                {errors.field_label && (
                  <p className="mt-1 text-sm text-red-600">{errors.field_label}</p>
                )}
              </div>

              {/* Field Type */}
              <div>
                <Label htmlFor="field_type">Field Type *</Label>
                <Select
                  value={formData.field_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, field_type: value }))}
                  disabled={!!editingField}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="dropdown">Dropdown</SelectItem>
                    <SelectItem value="yes_no">Yes/No</SelectItem>
                  </SelectContent>
                </Select>
                {editingField && (
                  <p className="mt-1 text-xs text-gray-500">Field type cannot be changed after creation</p>
                )}
              </div>

              {/* Dropdown Options (if type is dropdown) */}
              {formData.field_type === 'dropdown' && (
                <div>
                  <Label>Dropdown Options *</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={dropdownInput}
                      onChange={(e) => setDropdownInput(e.target.value)}
                      placeholder="Enter option"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddDropdownOption();
                        }
                      }}
                    />
                    <Button type="button" onClick={handleAddDropdownOption}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {errors.dropdown_options && (
                    <p className="mt-1 text-sm text-red-600">{errors.dropdown_options}</p>
                  )}

                  {formData.dropdown_options.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {formData.dropdown_options.map((option, index) => (
                        <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                          <span className="flex-1">{option}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveDropdownOption(index)}
                            className="text-red-600 hover:bg-red-50 p-1 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Default Value */}
              <div>
                <Label htmlFor="default_value">Default Value (Optional)</Label>
                {formData.field_type === 'yes_no' ? (
                  <Select
                    value={formData.default_value || 'none'}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, default_value: value === 'none' ? '' : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : formData.field_type === 'dropdown' ? (
                  <Select
                    value={formData.default_value || 'none'}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, default_value: value === 'none' ? '' : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {formData.dropdown_options.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="default_value"
                    type={formData.field_type === 'number' ? 'number' : formData.field_type === 'date' ? 'date' : 'text'}
                    value={formData.default_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, default_value: e.target.value }))}
                    placeholder="Enter default value"
                  />
                )}
              </div>

              {/* Is Required */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={formData.is_required}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_required: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <Label htmlFor="is_required" className="cursor-pointer">
                  Make this field required
                </Label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                {editingField ? 'Update Field' : 'Create Field'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default CustomFieldsManager;
